import { useEffect, useState } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import { LayoutDashboard, Wallet, Receipt, CreditCard, Calculator, Tag, LogOut, Menu, X } from 'lucide-react';
import { cn } from '@/lib/cn';
import { useLogout, useMe } from '@/hooks/useAuth';

const NAV_ITEMS = [
  { to: '/', label: 'Resumen', icon: LayoutDashboard, end: true },
  { to: '/deudas', label: 'Deudas', icon: CreditCard },
  { to: '/ingresos', label: 'Ingresos', icon: Wallet },
  { to: '/gastos', label: 'Gastos', icon: Receipt },
  { to: '/simulador', label: 'Simulador', icon: Calculator },
  { to: '/categorias', label: 'Categorías', icon: Tag },
];

function NavList({ onNavigate }: { onNavigate?: () => void }) {
  return (
    <nav className="flex flex-1 flex-col gap-1">
      {NAV_ITEMS.map(({ to, label, icon: Icon, end }) => (
        <NavLink
          key={to}
          to={to}
          end={end}
          onClick={onNavigate}
          className={({ isActive }) =>
            cn(
              'flex items-center gap-2 rounded-md px-3 py-2.5 text-sm font-medium transition-colors',
              isActive ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-accent hover:text-foreground',
            )
          }
        >
          <Icon size={16} />
          {label}
        </NavLink>
      ))}
    </nav>
  );
}

function UserBlock() {
  const { data: user } = useMe();
  const logout = useLogout();

  return (
    <div className="border-t border-border pt-3">
      <p className="truncate px-2 text-xs text-muted-foreground">{user?.displayName}</p>
      <button
        type="button"
        onClick={() => logout.mutate()}
        className="mt-1 flex w-full items-center gap-2 rounded-md px-3 py-2.5 text-sm text-muted-foreground hover:bg-accent hover:text-foreground"
      >
        <LogOut size={16} />
        Salir
      </button>
    </div>
  );
}

export function AppShell() {
  const [isDrawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    if (!isDrawerOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setDrawerOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isDrawerOpen]);

  return (
    <div className="flex min-h-dvh flex-col sm:flex-row">
      {/* Header móvil: barra fija con hamburguesa, reemplaza la nav de escritorio bajo sm */}
      <header className="flex items-center justify-between border-b border-border bg-card px-4 py-3 sm:hidden">
        <p className="text-lg font-semibold">EMC</p>
        <button
          type="button"
          aria-label="Abrir menú"
          onClick={() => setDrawerOpen(true)}
          className="flex h-11 w-11 items-center justify-center rounded-md text-foreground hover:bg-accent"
        >
          <Menu size={22} />
        </button>
      </header>

      {/* Overlay + drawer: solo existen en el DOM (y son interactivos) mientras están abiertos */}
      {isDrawerOpen && (
        <div className="fixed inset-0 z-40 sm:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setDrawerOpen(false)} />
          <aside className="absolute inset-y-0 left-0 flex w-64 flex-col bg-card p-4 shadow-lg">
            <div className="mb-6 flex items-center justify-between">
              <p className="text-lg font-semibold">EMC</p>
              <button
                type="button"
                aria-label="Cerrar menú"
                onClick={() => setDrawerOpen(false)}
                className="flex h-11 w-11 items-center justify-center rounded-md text-muted-foreground hover:bg-accent"
              >
                <X size={20} />
              </button>
            </div>
            <NavList onNavigate={() => setDrawerOpen(false)} />
            <UserBlock />
          </aside>
        </div>
      )}

      {/* Sidebar de escritorio, sin cambios */}
      <aside className="hidden w-56 shrink-0 border-r border-border bg-card p-4 sm:flex sm:flex-col">
        <p className="mb-6 px-2 text-lg font-semibold">EMC</p>
        <NavList />
        <UserBlock />
      </aside>

      <main className="min-w-0 flex-1 overflow-y-auto p-4 sm:p-6">
        <Outlet />
      </main>
    </div>
  );
}
